import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalService } from 'ui-components/services/modal-service';

import { GenericErrorRetryContainer } from '../../../containers/generic-error-retry-container';
import { TagService } from '../../../services/tag-service';
import { addNewTagSuccess } from '../../../store/tags/tags.actions';
import { createTags } from '../../../store/tags/tags.api';
import { messages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';

const makeEmptyTag = () => ({
  name: '',
  hexColor: TagService.getRandomTagColor(false),
});

export const useAddTags = ({ onClose }) => {
  const dispatch = useDispatch();
  const [newTags, setNewTags] = useAsyncSafeState([makeEmptyTag()]);
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const trackTagManagementWorkflow = useTrackWorkflow(Workflows.TAG_MANAGEMENT_FLOW);

  const onAddNewTag = useCallback(() => {
    setNewTags([...newTags, makeEmptyTag()]);
  }, [newTags, setNewTags]);

  const onRemoveTag = useCallback(
    (index) => {
      const filteredTags = newTags.filter((_, addTagIndex) => {
        return addTagIndex !== index;
      });
      if (filteredTags.length === 0) {
        filteredTags.push(makeEmptyTag());
      }
      setNewTags(filteredTags);
    },
    [newTags, setNewTags],
  );

  const onTagChanged = useCallback(
    (changedTag, changedIndex) => {
      const tags = newTags.map((tag, index) => {
        if (changedIndex === index) {
          return changedTag;
        }
        return tag;
      });
      setNewTags(tags);
    },
    [setNewTags, newTags],
  );

  const onSaveNewTags = useCallback(
    async (tags) => {
      setIsSaving(true);
      try {
        const responseTags = await createTags(tags);
        dispatch(addNewTagSuccess(responseTags));
        trackTagManagementWorkflow(WorkflowSteps.COMPLETE, {
          tags: responseTags,
        });
        onClose();
      } catch (e) {
        setIsSaving(false);
        trackTagManagementWorkflow(WorkflowSteps.ERROR, { error: e });
        ModalService.launchModal({
          component: GenericErrorRetryContainer,
          props: {
            onClick: () => onSaveNewTags(tags),
            title: <FormattedMessage {...messages.addTagErrorTitle} />,
            message: <FormattedMessage {...messages.addTagErrorMessage} />,
          },
        });
      }
    },
    [dispatch, onClose, setIsSaving, trackTagManagementWorkflow],
  );

  const memoizedAddTagsObj = useMemo(() => {
    return {
      newTags,
      isSaving,
      onAddNewTag,
      onTagChanged,
      onRemoveTag,
      onSaveNewTags,
    };
  }, [newTags, isSaving, onAddNewTag, onTagChanged, onRemoveTag, onSaveNewTags]);

  return memoizedAddTagsObj;
};
